import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InsightSuggestion } from '../../../data-access';
import SuggestionChip from '../SuggestedBlocks/SuggestionChip';
import ApproveDismissBar from '../SuggestedBlocks/ApproveDismissBar';
import './InsightSuggestionsBlock.scss';

interface InsightSuggestionsBlockProps {
  insightSuggestions: InsightSuggestion[];
  onSendPrompt: (text: string) => void;
}

const SIGNAL_EMOJI: Record<string, string> = {
  weather: '☀️',
  sleep: '😴',
  energy: '⚡',
  meal: '🍽️',
  commute: '🚗',
  steps: '🚶',
};

/**
 * "Your best time for focused tasks..." style insight card — a row of
 * tap-to-ask chips plus an Approve All/Dismiss All bar. There's no backend
 * contract tying a chip to a specific action yet (InsightSuggestion.cta_action
 * exists on the type but is never populated/consumed), so tapping a chip
 * sends its title+description as a normal follow-up chat prompt, "Approve
 * All" sends one consolidated prompt for every visible insight, and "Dismiss
 * All" just hides this block locally (no data mutation).
 */
const InsightSuggestionsBlock: FC<InsightSuggestionsBlockProps> = ({
  insightSuggestions,
  onSendPrompt,
}) => {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || !insightSuggestions.length) {
    return null;
  }

  const handleApproveAll = () => {
    const titles = insightSuggestions.map((s) => s.title).join('; ');
    onSendPrompt(t('assistantChat.insights.approveAllPrompt', { titles }));
  };

  return (
    <div className="insight-suggestions-block">
      <div className="insight-suggestions-block__chip-row">
        {insightSuggestions.map((suggestion) => (
          <SuggestionChip
            key={suggestion.id}
            emoji={SIGNAL_EMOJI[suggestion.signal_source] ?? '✨'}
            label={suggestion.title}
            onPress={() => onSendPrompt(`${suggestion.title}. ${suggestion.description}`)}
          />
        ))}
      </div>
      <ApproveDismissBar
        approveLabel={t('assistantChat.insights.approveAll')}
        dismissLabel={t('assistantChat.insights.dismissAll')}
        onApproveAll={handleApproveAll}
        onDismissAll={() => setDismissed(true)}
        compact
      />
    </div>
  );
};

export default InsightSuggestionsBlock;
